import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Copy, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCurrentUser } from '@/services/api/auth';
import { listRotinasAgendamento, type Rotina } from '@/services/api/rotinas';
import DiasPills from './components/DiasPills';
import RotinaDia from './components/RotinaDia';
import ImportarDiaDialog from './components/ImportarDiaDialog';
import NovaRotinaDialog from './components/NovaRotinaDialog';
import {
    DIAS_CURTOS, DIAS_SEMANA, HORA_MATERIALIZACAO, ddmm, diaFalado, hojeBRT, proximaMaterializacao,
} from './components/rotinaUtils';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';

export default function Rotinas() {
    const [dia, setDia] = useState<number>(() => hojeBRT().getDay());
    const [nova, setNova] = useState(false);
    const [importar, setImportar] = useState<{ aberto: boolean; dia?: number }>({ aberto: false });

    // Admin não tem operação própria: vê as Rotinas de todos (ou de um dono
    // pelo filtro) e, por ora, só vê — criar, editar e ligar não aparecem para ele.
    const eu = getCurrentUser();
    const { isAdmin, usuarioId } = useFiltroUsuarioAdmin();
    const filtroUsuario = usuarioId ?? undefined;
    const vendoAsMinhas = !isAdmin;
    const somenteLeitura = (r: Rotina) => isAdmin && String(r.user_id) !== String(eu?.id);

    const { data: rotinas = [], isLoading, isError } = useQuery({
        queryKey: ['rotinas-agendamento', filtroUsuario ?? null],
        queryFn: () => listRotinasAgendamento(filtroUsuario),
    });

    const proxima = proximaMaterializacao();
    const proximoDia = proxima.getDay();
    const vespera = new Date(proxima);
    vespera.setDate(vespera.getDate() - 1);
    const doProximoDia = rotinas.filter((e) => e.dia_semana === proximoDia);
    const noArNoProximo = doProximoDia.find((e) => e.ativa);

    return (
        <div className="mx-auto flex max-w-[1200px] flex-col gap-5 p-6">
            {/* `sm:flex-nowrap` + `shrink-0` nos botões: com flex-wrap o texto de
                apoio empurrava os dois para a linha de baixo, à esquerda, e eles
                deixavam de parecer a ação principal da tela. */}
            <header className="flex flex-wrap items-start justify-between gap-4 sm:flex-nowrap">
                <div className="max-w-2xl">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Meta · Rotinas</p>
                    <h1 className="text-2xl font-bold">Rotinas</h1>
                    <p className="text-sm text-muted-foreground">
                        Um molde por dia da semana. Todo dia às{' '}
                        <strong className="text-foreground">{HORA_MATERIALIZACAO} (Brasília)</strong> a Rotina no ar
                        do dia seguinte vira broadcasts — cada grupo é um broadcast, disparado na hora local da página.
                        Editar um grupo depois disso ainda vale para os broadcasts já criados.
                        Várias Rotinas podem ficar no ar no mesmo dia — para separar por região, por
                        exemplo. O que não pode é a mesma página estar em duas delas.
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <FiltroUsuarioAdmin className="w-full sm:w-56" />
                    {vendoAsMinhas && (<>
                        <Button variant="outline" disabled={isLoading || isError} onClick={() => setImportar({ aberto: true })}>
                            <Copy className="mr-1 h-4 w-4" /> Montar a partir de um dia
                        </Button>
                        <Button disabled={isLoading || isError} onClick={() => setNova(true)}>
                            <Plus className="mr-1 h-4 w-4" /> Nova Rotina
                        </Button>
                    </>)}
                </div>
            </header>

            {!isLoading && !isError && !(isAdmin && filtroUsuario == null) && (
                <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${
                    noArNoProximo ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}>
                    <Clock className="h-[18px] w-[18px] shrink-0" />
                    <div className="flex-1">
                        <strong className="tabular-nums">
                            Próxima materialização: {diaFalado(vespera)} às {HORA_MATERIALIZACAO},
                            para {DIAS_CURTOS[proximoDia]} {ddmm(proxima)}.
                        </strong>{' '}
                        {noArNoProximo
                            ? `"${noArNoProximo.nome}" está no ar e vai criar os broadcasts de ${DIAS_SEMANA[proximoDia]}.`
                            : `${DIAS_SEMANA[proximoDia]} não tem Rotina no ar — nada será criado.`}
                        {!noArNoProximo && doProximoDia.length > 0 && (
                            <> Há {doProximoDia.length} Rotina(s) desligada(s) nesse dia.</>
                        )}
                    </div>
                    {dia !== proximoDia && (
                        <Button variant="outline" size="sm" onClick={() => setDia(proximoDia)}>
                            Ver {DIAS_SEMANA[proximoDia].toLowerCase()}
                        </Button>
                    )}
                </div>
            )}

            {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
            {isError && (
                <p className="text-sm text-destructive">
                    Não foi possível carregar as Rotinas. Sem a lista não dá para saber se já existe uma no ar no
                    dia, então a criação fica desligada.
                </p>
            )}

            {!isLoading && !isError && (
                <>
                    <DiasPills
                        rotinas={rotinas}
                        selecionado={dia}
                        proximoDia={proximoDia}
                        onSelecionar={setDia}
                    />
                    <RotinaDia
                        dia={dia}
                        rotinas={rotinas.filter((e) => e.dia_semana === dia)}
                        onImportar={(d) => setImportar({ aberto: true, dia: d })}
                        podeCriar={vendoAsMinhas}
                        somenteLeitura={somenteLeitura}
                    />
                </>
            )}

            <NovaRotinaDialog open={nova} onOpenChange={setNova} dia={dia} />

            <ImportarDiaDialog
                open={importar.aberto}
                diaInicial={importar.dia ?? dia}
                rotinas={rotinas}
                onOpenChange={(aberto) => setImportar((s) => ({ ...s, aberto }))}
                onAbrirDia={setDia}
            />
        </div>
    );
}
